import {
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  OnInit,
  ViewEncapsulation,
  computed,
  inject,
  signal,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { forkJoin } from 'rxjs';
import { UserSignalStore } from '@core/stores/user-signal-store.service';
import { ProjectApiService } from '../../../@core/services/api/project-api.service';
import { AuthenticationService } from '../../../@core/services/rest/authentication.service';

import { ConstructionProjectList } from '@construction/models/construction-project.model';
import { ConstructionProjectsService } from '@construction/services/construction-project.service';
import { SubSink } from 'subsink';
import { UserProfileDialogComponent } from '../user-profile-dialog/user-profile-dialog.component';
import { NotificationType, TbsNotificationService } from '@ngx-terabase/ui/notification';
import { GetConstructionProjectsQueryParams } from '@construction/construction.types';
import packageInfo from '../../../../../package.json';
import { ProjectStatusEnum } from '@construction/construction.enums';
import { SidebarWrapperComponent } from './wrapper/sidebar-wrapper.component';
import { RouterLink, RouterLinkActive } from '@angular/router';
import { NavItemsContainerComponent } from './containers/nav-items-container/nav-items-container.component';
import { NavItemsGroupContainerComponent } from './containers/nav-items-group-container/nav-items-group-container.component';
import { NavItemsGroupComponent } from './nav/nav-group/nav-items-group.component';
import { NavItemComponent } from './nav/nav-item/nav-item.component';

import { MatDivider } from '@angular/material/divider';
import { ImageComponent } from '../../../shared/core-image/image/image.component';
import { TbsIconComponent } from '@ngx-terabase/ui/icon';

@Component({
  selector: 'app-sidebar',
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    SidebarWrapperComponent,
    RouterLink,
    NavItemsContainerComponent,
    NavItemsGroupContainerComponent,
    NavItemsGroupComponent,
    NavItemComponent,
    TbsIconComponent,
    RouterLinkActive,
    ImageComponent,
    MatDivider,
  ],
})
export class SidebarComponent implements OnInit, OnDestroy {
  private dialog = inject(MatDialog);
  private userStore = inject(UserSignalStore);
  private projectApiService = inject(ProjectApiService);
  private authService = inject(AuthenticationService);
  private constructionProjectsService = inject(ConstructionProjectsService);
  private notificationService = inject(TbsNotificationService);

  public readonly userName = computed(() => {
    const profile = this.userStore.user()?.contact_profile;

    return `${profile?.first_name} ${profile?.last_name}`;
  });
  public readonly profilePicture = computed(() => this.userStore.user()?.contact_profile?.profile_picture?._id);
  public readonly companyId = computed(() => this.userStore.user()?.contact_profile?.company);
  public readonly isSuperAdmin = this.userStore.isSuperAdmin;
  public readonly isCompanyAdmin = this.userStore.isCompanyAdmin;
  public readonly pinnedProjects = signal<ConstructionProjectList[]>([]);
  public readonly unpinnedProjects = signal<ConstructionProjectList[]>([]);
  public readonly hasBothSections = computed(
    () => this.pinnedProjects().length > 0 && this.unpinnedProjects().length > 0,
  );
  public readonly canSeePortfolioStatus = signal(false);
  public readonly isVersionShown = signal(false);
  public readonly version = packageInfo.version;
  private pendingPinChanges = new Map<string, boolean>();
  private subs = new SubSink();

  public ngOnInit(): void {
    this.refreshProjects();
  }

  public logout(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();
    this.authService.logOut();
  }

  public onProjectsHover(): void {
    if (this.pendingPinChanges.size === 0) return;

    let pinned = this.pinnedProjects();
    let unpinned = this.unpinnedProjects();

    for (const [id, isPinned] of this.pendingPinChanges) {
      if (isPinned) {
        const project = unpinned.find((p) => p._id === id);
        if (project) {
          pinned = [...pinned, project].sort(sortByName);
          unpinned = unpinned.filter((p) => p._id !== id);
        }
      } else {
        const project = pinned.find((p) => p._id === id);
        if (project) {
          unpinned = [...unpinned, project].sort(sortByName);
          pinned = pinned.filter((p) => p._id !== id);
        }
      }
    }

    this.pinnedProjects.set(pinned);
    this.unpinnedProjects.set(unpinned);
    this.pendingPinChanges.clear();
  }

  public onPinProject(project: ConstructionProjectList, pinned: boolean): void {
    this.subs.sink = this.projectApiService.pinProject(project._id, pinned).subscribe({
      next: () => {
        this.pendingPinChanges.set(project._id, pinned);
      },
      error: () => {
        this.notificationService.snackBar({
          title: 'Error',
          message: 'There was an error while pinning project. Please try again.',
          type: NotificationType.Error,
          duration: 5000,
        });
      },
    });
  }

  public openUsersProfile(): void {
    this.dialog.open(UserProfileDialogComponent, { width: '600px' });
  }

  public ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private refreshProjects(): void {
    const ownerCompanyId = this.userStore.user()?.company_profile?._id;
    const queryParams: GetConstructionProjectsQueryParams = {
      page: 1,
      page_size: 1000,
      status_filter: `${ProjectStatusEnum.CONSTRUCTION_STARTED},${ProjectStatusEnum.IN_SERVICE},${ProjectStatusEnum.VERIFICATION_PROCESSED}`,
    };

    this.subs.sink = forkJoin([
      this.constructionProjectsService.getConstructionProjects(queryParams),
      this.projectApiService.getPinnedProjects(this.userStore.user()?._id ?? ''),
    ]).subscribe(([projects, pinned]) => {
      const pinnedIds = new Set(pinned.data.map((p) => p.construction_project_id));
      const pinnedProjects: ConstructionProjectList[] = [];
      const unpinnedProjects: ConstructionProjectList[] = [];

      for (const project of projects.data) {
        if (pinnedIds.has(project._id)) {
          pinnedProjects.push(project);
        } else {
          unpinnedProjects.push(project);
        }
      }

      this.canSeePortfolioStatus.set(
        projects.data.some(
          (project) =>
            ownerCompanyId === project.project.account_owner_company_id &&
            project.status === ProjectStatusEnum.CONSTRUCTION_STARTED,
        ),
      );
      this.pendingPinChanges.clear();
      this.pinnedProjects.set(pinnedProjects.sort(sortByName));
      this.unpinnedProjects.set(unpinnedProjects.sort(sortByName));
    });
  }
}

function sortByName(a: ConstructionProjectList, b: ConstructionProjectList): number {
  const nameA = a.project.project_name as string;
  const nameB = b.project.project_name as string;

  if (nameA < nameB) return -1;
  if (nameA > nameB) return 1;

  return 0;
}
